/**
 * Same-origin pub/sub used by OneView tabs to announce tenant changes to each
 * other. `BroadcastChannel` only delivers messages between contexts with the
 * same origin (scheme + host + port), so this only catches OneView ↔ OneView
 * tab switches in the same browser.
 */
const BROADCAST_CHANNEL_NAME = "oneview:tenant-change";

let activeTenantId = null;
const listeners = new Set();
const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    : null;

if (channel) {
  channel.addEventListener("message", (event) => {
    if (activeTenantId == null) return;
    const incomingId = Number(event.data?.tenantId);
    if (Number.isFinite(incomingId) && incomingId !== activeTenantId) {
      for (const listener of listeners) listener(incomingId);
    }
  });
}

/**
 * Called by `SessionTenantContext` when this tab's tenant is (re)confirmed:
 * after initial session hydration and at the start of an explicit switch.
 * Broadcasts the new id to other same-origin OneView tabs so they can prompt
 * for a refresh.
 * @param {number|null} id
 */
export function setActiveTenantId(id) {
  const previous = activeTenantId;
  activeTenantId = id;
  if (channel && id != null && id !== previous) {
    channel.postMessage({ tenantId: id });
  }
}

/**
 * Subscribe to tenant-change notifications from other same-origin tabs.
 * Returns an unsubscribe function.
 * @param {(tenantId: number) => void} listener
 * @returns {() => void}
 */
export function subscribeTenantChange(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
